import { Td, Tr } from '@chakra-ui/react';
import { ReactNode, useMemo } from 'react';

import { Txt } from '../../text';
import { TableColumn, TableGroupRowData } from '../types';

export function TableRow<I extends EntityBase = EntityBase>({ 
  columns, rowIndex, rowData, loading, grouped, hasGroups, collapsed, selectedRow, onRowClick, totalRows, renderGroup, getRowBackgroundColor,
}: {
  columns: TableColumn<I>[],
  rowIndex: number,
  rowData: I,
  loading?: boolean
  grouped?: boolean
  hasGroups?: boolean
  collapsed?: boolean
  selectedRow?: I | TableGroupRowData<I>,
  onRowClick?: (row: I | TableGroupRowData<I>) => void
  totalRows: number;
  renderGroup?: (row: I, column: TableColumn<I>, items: I[]) => ReactNode
  getRowBackgroundColor?: (rowData: I, rowIndex: number) => string | undefined;
}) {
  const visibleColumns = useMemo(() => columns.filter((column) => !column.options?.hidden), [columns]);

  const backgroundColor = useMemo(() => {
    if (rowData.id && (selectedRow as I)?.id === rowData.id) {
      return 'light.300 !important';
    }
    const customColor = getRowBackgroundColor?.(rowData, rowIndex);
    if (customColor) {
      return `${customColor} !important`;
    }
    if (grouped) {
      return 'light.200';
    }
    return 'white !important';
  }, [rowData, rowIndex, selectedRow, grouped, getRowBackgroundColor]);

  return <>
    <Tr 
      _hover={{ backgroundColor: onRowClick ? 'gray.200' : 'gray.50' }} 
      opacity={collapsed ? 0 : 1}
      maxHeight={collapsed ? '0px' : 'unset'}
      visibility={collapsed ? 'collapse' : 'visible'}
      lineHeight={collapsed ? '0px' : 'unset'}
      cursor={onRowClick ? 'pointer' : 'default'}
      backgroundColor={backgroundColor}
      style={{
        transition: 'all 0.2s easy',
        border: 'none',
      }}
      onClick={() => onRowClick?.(rowData)}
    >
      {hasGroups && <Td 
        borderWidth={0}
        px={5} 
        py={collapsed ? 0 : 2}
        lineHeight={collapsed ? '0px' : 'unset'}
        opacity={collapsed ? 0 : 1}
        style={{
          transition: 'all 0.3s linear',
        }}
      />}
      {columns.map((column, columnIndex) => {
        if (column.options?.customBodyRender) {
          return <Td 
            key={`${column.name.toString()}_${columnIndex}`} 
            borderColor='gray.300'
            borderTopWidth={0}
            borderBottomWidth={collapsed ? 0 : 1}
            px={5} 
            py={collapsed ? 0 : 2}
            lineHeight={collapsed ? '0px' : 'unset'}
            maxHeight={collapsed ? '0px' : 'unset'}
            opacity={collapsed ? 0 : 1}
            overflow='hidden'
            style={{
              transition: 'all 0.3s linear',
            }}
            css={{
              maxHeight: '0px',
            }}
          >{column.options?.customBodyRender?.(
            rowData[column.name],
            {
              columnIndex,
              rowIndex,
              column,
              rowData,
              loading,
              totalRows,
            }
          )}</Td>;
        }

        return <Td  
          borderColor='gray.300'
          borderTopWidth={0}
          borderBottomWidth={collapsed ? 0 : 1}
          fontSize='sm' 
          lineHeight={collapsed ? '0px' : 'unset'}
          opacity={collapsed ? 0 : 1}
          overflow='hidden'
          px={5} 
          py={collapsed ? 0 : 2}
          key={`${column.name.toString()}_${columnIndex}`} 
          style={{
            transition: 'all 0.3s linear',
          }}
          // verticalAlign="baseline"
        >
          <Txt whiteSpace='pre-wrap'>{rowData[column.name]}</Txt>
        </Td>;
      })}
    </Tr>
    {renderGroup && <Tr>
      <Td colSpan={visibleColumns.length} p={0}>
        {renderGroup(rowData, columns[0], columns.map((column) => rowData[column.name]))}
      </Td>
    </Tr>}
  </>;
}